import React, { useState, useEffect } from 'react';
import { Star, X, CheckCircle } from 'lucide-react';
import BASE_URL from './config';
import StarRating from './StarRating';

/**
 * RateParticipants — modal for rating co-riders after a completed ride.
 *
 * Props:
 *   rideId: number — the ride to rate participants for
 *   isOpen: boolean — controls modal visibility
 *   onClose: function — callback to close the modal
 */
function RateParticipants({ rideId, isOpen, onClose }) {
  const [ratingStatus, setRatingStatus] = useState(null);
  const [submittedRatings, setSubmittedRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && rideId) {
      fetchRatingStatus();
    }
  }, [isOpen, rideId]);

  const fetchRatingStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/api/rides/${rideId}/ratings/status`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load rating status');
      const data = await response.json();
      setRatingStatus(data);
      // Pre-fill existing ratings
      const existing = {};
      if (data.myRatings) {
        Object.entries(data.myRatings).forEach(([username, score]) => {
          existing[username] = score;
        });
      }
      setSubmittedRatings(existing);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitRating = async (ratee, score) => {
    try {
      const response = await fetch(`${BASE_URL}/api/rides/${rideId}/ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ratee, score }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to submit rating');
      }

      setSubmittedRatings(prev => ({ ...prev, [ratee]: score }));
    } catch (err) {
      setError(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1F2937' }}>
              Rate Your Co-Riders
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#6B7280' }}>
              Your ratings help build trust in the community
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6B7280' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#6B7280' }}>
            Loading...
          </div>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#EF4444' }}>
            {error}
          </div>
        ) : !ratingStatus?.ratingsUnlocked ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
            <p>Ratings are not yet available for this ride.</p>
            <p style={{ fontSize: '0.8rem', marginTop: '8px' }}>
              Ratings unlock after the ride is completed (2 hours after departure).
            </p>
          </div>
        ) : ratingStatus.rateableParticipants?.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
            No other participants to rate.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {ratingStatus.rateableParticipants.map((participant) => {
              const currentScore = submittedRatings[participant.username] || 0;
              const hasRated = currentScore > 0;

              return (
                <div
                  key={participant.username}
                  style={{
                    padding: '14px',
                    backgroundColor: hasRated ? '#f0fdf4' : '#f8fafc',
                    borderRadius: '8px',
                    border: `1px solid ${hasRated ? '#bbf7d0' : '#e5e7eb'}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#1F2937', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {participant.name}
                        {hasRated && <CheckCircle size={14} style={{ color: '#22c55e' }} />}
                      </div>
                      {participant.aggregateRating && participant.aggregateRating.count > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <StarRating
                            rating={participant.aggregateRating.average}
                            count={participant.aggregateRating.count}
                            size="sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rating stars */}
                  <div style={{ marginTop: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#6B7280', marginRight: '8px' }}>
                      {hasRated ? 'Your rating:' : 'Rate:'}
                    </span>
                    <StarRating
                      rating={currentScore}
                      interactive={true}
                      onRate={(score) => submitRating(participant.username, score)}
                      size="md"
                      showCount={false}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#00356B',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default RateParticipants;
